import { supabase } from "@/integrations/supabase/client";

// Application Server Public Key for Web Push
const APPLICATION_SERVER_KEY = "BIfKSa-9X2iD3d-UC5G0mY4ZdHApxPSCHuCbkaYYlsEsA2R3a9UXBNZkIAKpqPVyMsz5-JGopRMaGW84O0iR1wA";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  // Don't register in iframes
  try {
    if (window.self !== window.top) return null;
  } catch {
    return null;
  }

  try {
    const reg = await navigator.serviceWorker.register("/sw-push.js", { scope: "/" });
    console.log("[Push] Service Worker registered");
    return reg;
  } catch (err) {
    console.error("[Push] SW registration failed:", err);
    return null;
  }
}

export async function subscribeToPush(userId: string): Promise<PushSubscription | null> {
  try {
    const registration = await registerServiceWorker();
    if (!registration) {
      console.warn("[Push] No SW registration available");
      return null;
    }

    // Wait for the SW to be ready
    await navigator.serviceWorker.ready;

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log("[Push] Creating new subscription...");
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(APPLICATION_SERVER_KEY).buffer as ArrayBuffer,
      });
      console.log("[Push] Subscription created:", subscription.endpoint.slice(0, 60) + "...");
    } else {
      console.log("[Push] Using existing subscription:", subscription.endpoint.slice(0, 60) + "...");
    }

    // Save to database
    const keys = subscription.toJSON().keys;
    if (!keys?.p256dh || !keys?.auth) {
      console.error("[Push] Missing subscription keys");
      return null;
    }

    const { error } = await supabase.from("push_subscriptions").upsert(
      {
        user_id: userId,
        endpoint: subscription.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
      { onConflict: "endpoint" }
    );

    if (error) {
      console.error("[Push] Error saving subscription:", error.message);
    } else {
      console.log("[Push] Subscription saved to DB ✓");
    }

    return subscription;
  } catch (err) {
    console.error("[Push] Subscribe error:", err);
    return null;
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      // Remove from DB
      await supabase
        .from("push_subscriptions")
        .delete()
        .eq("endpoint", subscription.endpoint);
      await subscription.unsubscribe();
      console.log("[Push] Unsubscribed ✓");
    }
  } catch (err) {
    console.error("[Push] Unsubscribe error:", err);
  }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  try {
    if (!("serviceWorker" in navigator)) return null;
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}
